import * as React from "react";
import { Route, Switch, withRouter, RouteComponentProps } from "react-router";
import { hot } from "react-hot-loader/root";
import Layout from "./components/Layout";
import Home from "./components/Home";
import Docs from "./components/Docs/Docs";
import Support from "./components/support/Support";
import SupportUnauthorized from "./components/support/SupportUnauthorized";
import SubmitServiceCall from "./components/servicecalls/SubmitServiceCall";
import ServiceCallList from "./components/servicecalls/ServiceCallList";
import "./App.css";
import AuthorizeRoute from "./components/api-authorization/AuthorizeRoute";
import { ApplicationPaths } from "./components/api-authorization/ApiAuthorizationConstants";
import ApiAuthorizationRoutes from "./components/api-authorization/ApiAuthorizationRoutes";
import authService, { IProfile } from "./components/api-authorization/AuthorizeService";
import Services from "./services/Services";
import SignUp from "./components/signup/SignUp";
import SignUpConfirmation from "./components/signup/SignUpConfirmation";
import AdminPanel from "./components/AdminPanel";
import { connect } from "react-redux";
import { IApplicationState } from "./store";
import { signin, signout } from "./store/auth/actions";
import IUser from "./models/IUser";
import { selectAuth } from "./store/auth/selectors";
import Roles, { ExcludeRoles } from "./models/Roles";
import ManageContacts from "./components/pages/ManageContacts";
import ICompany from "./models/ICompany";
import { IContextState } from "./store/context/types";
import { setCompany, setAvailableCompanies } from "./store/context/actions";
import AddContacts from "./components/pages/AddContact";
import EditContact from "./components/pages/EditContact";
import snackbarService from "./services/ui-messaging/SnackbarService";
import TryItOut from "./components/try-it-out/TryItOut";
import SubmitTicketConfirmation from "./components/servicecalls/SubmitTicketConfirmation";
import DocsInfo from "./components/Docs/DocsInfo";
import PartnershipRoutes from "./components/partnership/PartnershipRoutes";
import SubmitServiceRequest from "./components/servicecalls/SubmitServiceRequest";
import ServiceCallDetail from "./components/servicecalls/ServiceCallDetail";
import SubmitAPICredentials from "./components/credentials/SubmitAPICredentials";
import APICredentialsSuccess from "./components/credentials/APICredentialsSuccess";
import IntegrationDefinition from "./components/Docs/IntegrationDefinition";
import APIDefinition from "./components/Docs/APIDefinition";
import ManageNotifications from "./components/pages/ManageNotifications";

const getContext = async (user: IUser): Promise<IContextState> => {
  try {
    const cmpRepo = await new Services().CompanyRepository();
    const companies = await cmpRepo.get(user.token);
    return {
      company: companies[0],
      companies,
    };
  } catch (err) {
    snackbarService.error(err);
    return {
      companies: [],
    };
  }
};

interface IAppProps extends RouteComponentProps {
  isAuthenticated: boolean;
  userName?: string;
  signin: Function;
  signout: Function;
  setCompany: typeof setCompany;
  setCompanies: typeof setAvailableCompanies;
}

const App = (props: IAppProps) => {
  const {
    location: { pathname },
  } = props;

  const populateGlobalRespositories = () => {
    let svc: Services = new Services();
    svc.ServiceCallRepository();
    svc.SignUpRepository();
  };

  const checkHideFooter = () => {
    return pathname.startsWith("/docs");
  };

  const createUser = async (profile: IProfile): Promise<IUser> => {
    const token = await authService.getAccessToken();
    return {
      username: profile.display_name,
      companyId: profile.companyId ? parseInt(profile.companyId, 10) : -1,
      contactId: profile.contactId ? parseInt(profile.contactId, 10) : -1,
      roles: profile.role || [],
      token: token || "",
    };
  };

  const { signin, signout, setCompany, setCompanies } = props;

  const populateState = async () => {
    const isAuthenticated = await authService.isAuthenticated();
    const user = await authService.getUser();

    if (isAuthenticated && user) {
      const newUser = await createUser(user);
      const { company, companies } = await getContext(newUser);
      setCompany(company);
      setCompanies(companies);
      // signin needs to happen after the companies are set so that the unauthorized component
      // doesn't flash on the screen while we wait for the company to show up
      signin(newUser);
    } else {
      signout();
      setCompany(undefined);
      setCompanies([]);
    }
  };

  React.useEffect(() => {
    let subscription = 0;
    subscription = authService.subscribe(() => populateState());
    populateState();
    populateGlobalRespositories();
    return () => {
      if (subscription) {
        authService.unsubscribe(subscription);
      }
    };
  }, []);

  return (
    <Layout hideFooter={checkHideFooter()}>
      <Switch>
        <Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />
        <Route exact path="/" component={Home} />
        <AuthorizeRoute exact path="/docs" component={Docs} unauthorizedComponent={DocsInfo} roles={[Roles.Documentation]} displayUnauthorizedAsBackup={true} />
        <Route path="/support/api-definition" component={APIDefinition} />
        <Route path="/support/integration-definition" component={IntegrationDefinition} />
        <Route path="/partnership" component={PartnershipRoutes} />
        <AuthorizeRoute path="/support" component={Support} unauthorizedComponent={SupportUnauthorized} roles={[Roles.Support]} />
        <AuthorizeRoute path="/submit-sc" component={SubmitServiceCall} roles={[Roles.Support]} />
        <AuthorizeRoute path="/submit-sr" component={SubmitServiceRequest} roles={[Roles.Support]} />
        <AuthorizeRoute path="/submit-sc-confirmation" component={SubmitTicketConfirmation} roles={[Roles.Support]} />
        <AuthorizeRoute path="/list-sc" component={ServiceCallList} roles={[Roles.Support]} />
        <AuthorizeRoute path="/test-credentials/entry" component={SubmitAPICredentials} />
        <AuthorizeRoute path="/test-credentials/success" component={APICredentialsSuccess} />
        <AuthorizeRoute path="/admin-panel" component={AdminPanel} roles={[Roles.Admin]} />
        <AuthorizeRoute path="/users/list" component={ManageContacts} roles={[Roles.Admin, Roles.ContactManager]} />
        <AuthorizeRoute path="/users/add" component={AddContacts} roles={[Roles.Admin, Roles.ContactManager]} />
        <AuthorizeRoute path="/users/details/:id" component={EditContact} roles={[Roles.Admin, Roles.ContactManager]} />
        <Route path="/apply" component={SignUp} />
        <Route path="/apply-confirmation" component={SignUpConfirmation} />
        <Route path="/sign-up" component={SignUp} />
        <AuthorizeRoute path="/try-it-out/:product/:apiType" component={TryItOut} roles={[Roles.Documentation]} />
        <AuthorizeRoute path="/sc-detail/:ticketNumber" component={ServiceCallDetail} roles={[Roles.Support]} />
        <AuthorizeRoute path="/notifications/manage" component={ManageNotifications} />
      </Switch>
    </Layout>
  );
};

const ConnectedApp = connect(
  (state: IApplicationState) => {
    return selectAuth(state);
  },
  dispatch => {
    return {
      signin: (user: IUser) => dispatch(signin(user)),
      signout: () => dispatch(signout()),
      setCompany: (company: ICompany | undefined) => dispatch(setCompany(company)),
      setCompanies: (companies: Array<ICompany>) => dispatch(setAvailableCompanies(companies)),
    };
  },
)(withRouter(App));

export default process.env.NODE_ENV === "development" ? hot(ConnectedApp) : ConnectedApp;
